import { queryKeys } from 'core/api/queryKeys';
import {
  EXPECTED_TOP_THREE_PROFILE_COUNT,
  isBuiltVersion,
  isValidVersionId,
} from 'features/top-three-profiles/topThreeProfiles';

export const WORKBOOK_ACCEPT = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const WORKBOOK_EXTENSION = '.xlsx';
export const CLIENT_MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
export const REJECTED_EXTENSIONS = ['.xls', '.xlsm', '.csv', '.xlsb'];
export const SUPPORTED_WORKBOOK_SCHEMA_VERSION = 3;
export const SUPPORTED_QUESTIONNAIRE_SCHEMA_VERSION = 1;

const UNSAFE_FILENAME_CHARS = '<>:"/\\|?*';

export const QUESTIONNAIRE_SUMMARY_ROWS = [
  { key: 'workbook.summary.patternsCreated', paths: ['pattern_creates', 'patterns.created', 'patterns_created'] },
  { key: 'workbook.summary.patternsUpdated', paths: ['pattern_updates', 'patterns.updated', 'patterns_updated'] },
  { key: 'workbook.summary.patternsArchived', paths: ['archives', 'patterns.archived', 'patterns_archived'] },
  { key: 'workbook.summary.patternsDeleted', paths: ['deletes', 'patterns.deleted', 'patterns_deleted'] },
  { key: 'workbook.summary.sectionsCreated', paths: ['section_creates', 'sections.created', 'sections_created'] },
  { key: 'workbook.summary.sectionsUpdated', paths: ['section_updates', 'sections.updated', 'sections_updated'] },
  { key: 'workbook.summary.sectionsMoved', paths: ['sections.moved', 'sections_moved'] },
  { key: 'workbook.summary.questionsCreated', paths: ['question_creates', 'questions.created', 'questions_created'] },
  { key: 'workbook.summary.questionsUpdated', paths: ['question_updates', 'questions.updated', 'questions_updated'] },
  { key: 'workbook.summary.questionsMoved', paths: ['questions.moved', 'questions_moved'] },
  { key: 'workbook.summary.questionsArchived', paths: ['questions.archived', 'questions_archived'] },
  { key: 'workbook.summary.questionsDeleted', paths: ['questions.deleted', 'questions_deleted'] },
  { key: 'workbook.summary.answersCreated', paths: ['answer_creates', 'answers.created', 'answers_created'] },
  { key: 'workbook.summary.answersUpdated', paths: ['answer_updates', 'answers.updated', 'answers_updated'] },
  { key: 'workbook.summary.answersArchived', paths: ['answers.archived', 'answers_archived'] },
  { key: 'workbook.summary.answersDeleted', paths: ['answers.deleted', 'answers_deleted'] },
  { key: 'workbook.summary.weightsCreated', paths: ['weight_creates', 'weights.created', 'weights_created'] },
  { key: 'workbook.summary.weightsUpdated', paths: ['weight_updates', 'weights.updated', 'weights_updated'] },
  { key: 'workbook.summary.unchangedRows', paths: ['no_changes', 'unchanged', 'unchanged_rows', 'rows.unchanged'] },
];

export const TOP_THREE_PROFILE_SUMMARY_ROWS = [
  { key: 'workbook.summary.tagsCreated', paths: ['creates', 'tags.created', 'tags_created'] },
  { key: 'workbook.summary.tagsUpdated', paths: ['updates', 'tags.updated', 'tags_updated'] },
  { key: 'workbook.summary.tagsArchived', paths: ['archives', 'tags.archived', 'tags_archived'] },
  { key: 'workbook.summary.tagsDeleted', paths: ['deletes', 'tags.deleted', 'tags_deleted'] },
  { key: 'workbook.summary.contentCreated', paths: ['content_creates', 'result_items.created', 'content.created', 'reusable_content.created', 'content_created'] },
  { key: 'workbook.summary.contentUpdated', paths: ['content_updates', 'result_items.updated', 'content.updated', 'reusable_content.updated', 'content_updated'] },
  { key: 'workbook.summary.contentArchived', paths: ['result_items.archived', 'content.archived', 'reusable_content.archived', 'content_archived'] },
  { key: 'workbook.summary.contentDeleted', paths: ['result_items.deleted', 'content.deleted', 'reusable_content.deleted', 'content_deleted'] },
  { key: 'workbook.summary.summariesCreated', paths: ['personal_summaries.created', 'summaries.created', 'personal_summaries_created'] },
  { key: 'workbook.summary.summariesUpdated', paths: ['personal_summaries.updated', 'summaries.updated', 'personal_summaries_updated'] },
  { key: 'workbook.summary.summaryAttached', paths: ['personal_summaries_attached', 'summary_attach', 'summary_attachments.attached', 'personal_summaries.attached', 'summary_attached', 'summaries_attached', 'connections_added.personal_summary'] },
  { key: 'workbook.summary.summaryDetached', paths: ['personal_summaries_detached', 'summary_detach', 'summary_attachments.detached', 'personal_summaries.detached', 'summaries_detached'] },
  { key: 'workbook.summary.keyStrengthsAttached', paths: ['key_strengths_attached'] },
  { key: 'workbook.summary.keyStrengthsDetached', paths: ['key_strengths_detached'] },
  { key: 'workbook.summary.growthAttached', paths: ['growth_opportunities_attached'] },
  { key: 'workbook.summary.growthDetached', paths: ['growth_opportunities_detached'] },
  { key: 'workbook.summary.careerPathsAttached', paths: ['career_paths_attached'] },
  { key: 'workbook.summary.careerPathsDetached', paths: ['career_paths_detached'] },
  { key: 'workbook.summary.skillsAttached', paths: ['skills_attached'] },
  { key: 'workbook.summary.skillsDetached', paths: ['skills_detached'] },
  { key: 'workbook.summary.connectionsAdded', paths: ['connections_added', 'connections.added', 'item_attach', 'result_connections.attached', 'items.attached', 'items_attached'] },
  { key: 'workbook.summary.connectionsRemoved', paths: ['connections_removed', 'connections.removed', 'item_detach', 'result_connections.detached', 'items.detached', 'items_detached'] },
  { key: 'workbook.summary.connectionsReordered', paths: ['connections_reordered', 'relationships_reordered', 'item_reorder', 'result_connections.reordered', 'items.reordered', 'connections.reordered'] },
  { key: 'workbook.summary.profilesMatched', paths: ['matched_profiles', 'profiles.matched', 'profiles_matched'] },
  { key: 'workbook.summary.profilesIgnored', paths: ['ignored_profiles', 'profiles.ignored', 'profiles_ignored'] },
  { key: 'workbook.summary.profilesSynchronized', paths: ['synchronized_profiles', 'profiles.synchronized', 'profiles_synchronized'] },
  { key: 'workbook.summary.profilesChanged', paths: ['profiles_changed'] },
  { key: 'workbook.summary.relationshipsRemoved', paths: ['relationships_removed'] },
  { key: 'workbook.summary.profilesFound', paths: ['found_profiles', 'profiles.found', 'profiles_found'] },
  { key: 'workbook.summary.profilesMissing', paths: ['missing_profiles', 'profiles.missing', 'profiles_missing'] },
  { key: 'workbook.summary.invalidKeys', paths: ['invalid_keys', 'keys.invalid', 'invalid_canonical_keys'] },
  { key: 'workbook.summary.duplicateKeys', paths: ['duplicate_keys', 'keys.duplicate', 'duplicate_canonical_keys'] },
  { key: 'workbook.summary.unchangedProfiles', paths: ['no_changes', 'profiles.unchanged', 'unchanged_profiles', 'unchanged', 'unchanged_rows'] },
];

export function datedWorkbookFilename(prefix, date = new Date()) {
  const stamp = date.toISOString().slice(0, 10);
  return `${prefix}-${stamp}.xlsx`;
}

export function parseContentDispositionFilename(header) {
  if (!header || typeof header !== 'string') {
    return null;
  }
  const encoded = header.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^["']|["']$/g, ''));
    } catch {
      return encoded[1].trim().replace(/^["']|["']$/g, '');
    }
  }
  const quoted = header.match(/filename\s*=\s*"([^"]+)"/i);
  if (quoted) {
    return quoted[1];
  }
  const plain = header.match(/filename\s*=\s*([^;]+)/i);
  if (plain) {
    return plain[1].trim().replace(/^["']|["']$/g, '');
  }
  return null;
}

export function sanitizeDownloadFilename(name, fallback) {
  const source = String(name || '').trim();
  const leaf = source.split(/[/\\]/).pop() || '';
  const cleaned = Array.from(leaf)
    .map((char) => {
      const code = char.charCodeAt(0);
      if (code < 32 || UNSAFE_FILENAME_CHARS.includes(char)) {
        return '_';
      }
      return char;
    })
    .join('')
    .replace(/^\.+/, '')
    .trim();
  if (!cleaned || cleaned === '.' || cleaned === '..' || cleaned.includes('..')) {
    return fallback;
  }
  return cleaned;
}

export function resolveDownloadFilename(header, fallback) {
  return sanitizeDownloadFilename(parseContentDispositionFilename(header), fallback) || fallback;
}

export function fileExtension(name = '') {
  const lower = String(name).trim().toLowerCase();
  const match = lower.match(/(\.[a-z0-9]+)$/);
  return match ? match[1] : '';
}

export function validateWorkbookFile(file, { maxBytes = CLIENT_MAX_UPLOAD_BYTES } = {}) {
  if (!file) {
    return { ok: false, code: 'missing' };
  }
  const name = file.name || '';
  const extension = fileExtension(name);
  if (REJECTED_EXTENSIONS.includes(extension) || extension !== WORKBOOK_EXTENSION) {
    return { ok: false, code: 'extension' };
  }
  if (Number(file.size) > maxBytes) {
    return { ok: false, code: 'oversized' };
  }
  return { ok: true, code: null };
}

export function truncateHash(value, visible = 12) {
  const hash = String(value || '');
  if (hash.length <= visible * 2) {
    return hash || '—';
  }
  return `${hash.slice(0, visible)}…${hash.slice(-6)}`;
}

export function saveBlobFile(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  try {
    if (process.env.NODE_ENV === 'test') {
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function asArray(value) {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([path, messages]) => {
      if (messages && typeof messages === 'object' && !Array.isArray(messages) && messages.sheet) {
        return [messages];
      }
      const list = Array.isArray(messages) ? messages : [messages];
      return list.filter(Boolean).map((message) => {
        if (message && typeof message === 'object' && (message.sheet || message.cell || message.message)) {
          return message;
        }
        return {
          sheet: '',
          row: '',
          column: path,
          cell: path,
          code: '',
          message: String(message),
          value: '',
        };
      });
    });
  }
  return [];
}

function asWarningList(value) {
  return asArray(value).map((warning) => {
    if (typeof warning === 'string') {
      return { code: '', message: warning };
    }
    return {
      code: warning.code || warning.type || '',
      message: warning.message || String(warning),
      destructive: Boolean(warning.destructive || warning.requires_confirmation),
    };
  });
}

export function readCount(source, paths, fallback = 0) {
  if (!source || typeof source !== 'object') {
    return fallback;
  }
  for (const path of paths) {
    const value = path.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), source);
    if (value != null && value !== '') {
      const number = Number(value);
      if (Number.isFinite(number)) {
        return number;
      }
    }
  }
  return fallback;
}

export function summaryRowsFor(kind, summary) {
  const rows = kind === 'top-three-profiles' ? TOP_THREE_PROFILE_SUMMARY_ROWS : QUESTIONNAIRE_SUMMARY_ROWS;
  return rows.map((row) => ({
    key: row.key,
    value: readCount(summary, row.paths, 0),
  }));
}

const DESTRUCTIVE_PATHS = [
  ['archives'],
  ['deletes'],
  ['summary_detach'],
  ['item_detach'],
  ['patterns.deleted', 'patterns_deleted'],
  ['patterns.archived', 'patterns_archived'],
  ['questions.deleted', 'questions_deleted'],
  ['questions.archived', 'questions_archived'],
  ['answers.deleted', 'answers_deleted'],
  ['answers.archived', 'answers_archived'],
  ['sections.deleted', 'sections_deleted'],
  ['tags.deleted', 'tags_deleted'],
  ['tags.archived', 'tags_archived'],
  ['result_items.deleted', 'content.deleted', 'content_deleted'],
  ['result_items.archived', 'content.archived', 'content_archived'],
  ['personal_summaries.deleted', 'summaries.deleted'],
  ['summary_attachments.detached', 'personal_summaries.detached', 'summary_detached', 'summaries_detached'],
  ['result_connections.detached', 'items.detached', 'connections.detached', 'items_detached'],
];

export function hasDestructiveChanges(preview) {
  if (!preview) {
    return false;
  }
  if (preview.requiresConfirmation) {
    return true;
  }
  if ((preview.warnings || []).some((warning) => warning.destructive || /delete|archiv|detach|invalidat|stale|large/i.test(`${warning.code} ${warning.message}`))) {
    return true;
  }
  return DESTRUCTIVE_PATHS.some((paths) => readCount(preview.summary, paths, 0) > 0);
}

export function normalizeWorkbookErrors(errors) {
  return asArray(errors).map((error, index) => ({
    sheet: error.sheet || '',
    row: error.row ?? '',
    column: error.column || error.header || error.field || '',
    cell: error.cell || (error.row && error.column ? `${error.column}${error.row}` : ''),
    code: error.code || '',
    message: error.message || error.detail || '',
    value: error.value ?? error.rejected_value ?? error.invalid_value ?? '',
    questionRef: error.question_ref || error.questionRef || error.reference || '',
    order: index,
  }));
}

function readSchemaVersion(source = {}) {
  const value = source.schema_version ?? source.schemaVersion;
  return value == null || value === '' ? '' : String(value);
}

function supportedSchemaVersionFor(workbookType) {
  return workbookType === 'top_three_profiles'
    ? SUPPORTED_WORKBOOK_SCHEMA_VERSION
    : SUPPORTED_QUESTIONNAIRE_SCHEMA_VERSION;
}

function readSupportedSchemaVersion(source = {}, fallback = SUPPORTED_QUESTIONNAIRE_SCHEMA_VERSION) {
  const value = source.supported_schema_version ?? source.supportedSchemaVersion;
  if (value == null || value === '') {
    return String(fallback);
  }
  return String(value);
}

export function isUnsupportedWorkbookSchema(preview) {
  if (!preview) {
    return false;
  }
  if (preview.schemaUnsupported) {
    return true;
  }
  const fileVersion = readSchemaVersion(preview);
  if (!fileVersion) {
    return false;
  }
  const workbookType = preview.workbookType || '';
  const supported = readSupportedSchemaVersion(
    preview,
    supportedSchemaVersionFor(workbookType),
  );
  return fileVersion !== supported;
}

export function normalizePreview(data = {}, fallbackMessage = '') {
  const payload = data && typeof data === 'object' ? data : {};
  const nested = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  const summary = nested.summary || nested.counts || nested.change_summary || nested;
  const errors = normalizeWorkbookErrors(nested.errors || payload.errors);
  const workbookType = nested.workbook_type || payload.workbook_type || '';
  const schemaVersion = readSchemaVersion(nested) || readSchemaVersion(payload);
  const supportedSchemaVersion = readSupportedSchemaVersion(
    nested,
    readSupportedSchemaVersion(payload, supportedSchemaVersionFor(workbookType)),
  );
  const schemaUnsupported = nested.schema_unsupported === true
    || payload.schema_unsupported === true
    || (workbookType === 'top_three_profiles' && schemaVersion !== '' && schemaVersion !== supportedSchemaVersion);
  const commitToken = nested.commit_token || nested.preview_token || payload.commit_token || payload.preview_token || '';
  const missingRequired = nested.missing_required_content
    || nested.missing_required
    || nested.missing_by_profile
    || nested.profiles_missing_required
    || [];
  return {
    workbookType,
    schemaVersion,
    supportedSchemaVersion,
    schemaUnsupported,
    fileSha256: nested.file_sha256 || payload.file_sha256 || '',
    commitToken,
    previewToken: commitToken,
    expectedProfiles: readCount(nested, ['expected_profiles', 'profiles.expected', 'total_expected'], 0),
    foundProfiles: readCount(nested, ['found_profiles', 'profiles.found', 'profiles_found'], 0),
    missingProfiles: asArray(nested.missing_profiles || nested.profiles_missing || nested.missing_canonical_keys),
    invalidKeys: asArray(nested.invalid_keys || nested.invalid_canonical_keys),
    duplicateKeys: asArray(nested.duplicate_keys || nested.duplicate_canonical_keys),
    missingRequiredByProfile: asArray(missingRequired),
    profilesChanged: readCount(nested, ['profiles_changed'], 0),
    relationshipsRemoved: readCount(nested, ['relationships_removed', 'connections_removed'], 0),
    fullSync: nested.full_sync === true || payload.full_sync === true,
    baseRevision: nested.base_revision ?? nested.export_revision ?? nested.configuration_revision ?? payload.base_revision ?? null,
    currentRevision: nested.current_revision ?? payload.current_revision ?? null,
    canCommit: nested.can_commit === true && errors.length === 0 && !schemaUnsupported,
    requiresConfirmation: nested.requires_destructive_confirmation === true
      || nested.requires_confirmation === true
      || nested.confirmation_required === true,
    warnings: asWarningList(nested.warnings || payload.warnings),
    errors,
    summary,
    applied: nested.applied || nested.applied_counts || nested.applied_summary || null,
    revision: nested.revision ?? nested.current_revision ?? payload.revision ?? null,
    message: nested.message || payload.message || fallbackMessage,
    versionInvalidation: Boolean(nested.version_invalidation || nested.invalidates_versions),
  };
}

export function previewFromApiError(error) {
  const payload = error?.payload && typeof error.payload === 'object' ? error.payload : {};
  const data = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  const fieldErrors = error?.errors && typeof error.errors === 'object' ? error.errors : {};
  const schemaVersion = readSchemaVersion(fieldErrors) || readSchemaVersion(data);
  const supportedSchemaVersion = readSupportedSchemaVersion(fieldErrors, readSupportedSchemaVersion(data));
  return normalizePreview({
    ...data,
    schema_version: schemaVersion,
    supported_schema_version: supportedSchemaVersion,
    schema_unsupported: schemaVersion !== '' && schemaVersion !== supportedSchemaVersion,
    errors: Array.isArray(data.errors?.cells)
      ? data.errors.cells
      : (Array.isArray(data.cells)
        ? data.cells
        : (Array.isArray(data.errors)
          ? data.errors
          : (Array.isArray(error?.errors?.cells)
            ? error.errors.cells
            : (Array.isArray(error?.errors) ? error.errors : error?.errors)))),
    can_commit: false,
    message: error?.message,
  }, error?.message);
}

export function filterWorkbookErrors(errors, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) {
    return errors;
  }
  return errors.filter((error) => (
    [error.sheet, error.cell, error.column, error.row, error.message, error.value, error.code]
      .join(' ')
      .toLowerCase()
      .includes(needle)
  ));
}

export function groupErrorsBySheet(errors) {
  const groups = [];
  const indexBySheet = new Map();
  errors.forEach((error) => {
    const sheet = error.sheet || '';
    if (!indexBySheet.has(sheet)) {
      indexBySheet.set(sheet, groups.length);
      groups.push({ sheet, rows: [] });
    }
    groups[indexBySheet.get(sheet)].rows.push(error);
  });
  return groups;
}

export function workbookVersionBlockReason(version, versionId, action = 'any') {
  if (!versionId) {
    return 'workbook.versionRequired';
  }
  if (!version) {
    return null;
  }
  if (version.status === 'archived') {
    return 'profiles.publishedReadOnly';
  }
  if (version.workbook_locked || version.is_locked || version.import_locked) {
    return 'workbook.versionLocked';
  }
  if (version.is_stale || version.workbook_stale) {
    return 'workbook.versionStale';
  }
  if (version.workbook_eligible === false) {
    return 'workbook.versionIneligible';
  }
  if (action !== 'export' && (version.workbook_import_allowed === false || version.can_import_workbook === false)) {
    return 'workbook.versionIneligible';
  }
  if (action !== 'import' && version.workbook_export_allowed === false) {
    return 'workbook.versionIneligible';
  }
  if (action !== 'export' && version.content_editable === false) {
    return 'workbook.versionIneligible';
  }
  return null;
}

export function topThreeTemplateBlockReason({ versionId, version, canExport } = {}) {
  if (!canExport) {
    return 'workbook.errors.noExportPermission';
  }
  if (!isValidVersionId(versionId) || (version && !isBuiltVersion(version))) {
    return 'workbook.versionRequired';
  }
  return null;
}

export function topThreeExportBlockReason({
  versionId,
  version,
  canExport,
  coverageLoaded = false,
  actualProfiles = null,
} = {}) {
  if (!canExport) {
    return 'workbook.errors.noExportPermission';
  }
  if (!isValidVersionId(versionId) || (version && !isBuiltVersion(version))) {
    return 'workbook.versionRequired';
  }
  if (!coverageLoaded) {
    return 'profiles.coverageLoadFailed';
  }
  if (Number(actualProfiles) !== EXPECTED_TOP_THREE_PROFILE_COUNT) {
    return 'profiles.exportNeedsAllIdentities';
  }
  return null;
}

export function topThreeImportBlockReason({ versionId, version, canImport } = {}) {
  if (!canImport) {
    return 'workbook.errors.noImportPermission';
  }
  if (!isValidVersionId(versionId)) {
    return 'workbook.versionRequired';
  }
  if (version?.status === 'archived') {
    return 'workbook.errors.importNotAllowed';
  }
  if (version?.workbook_import_allowed === false || version?.can_import_workbook === false) {
    return 'workbook.errors.importNotAllowed';
  }
  if (version?.content_editable === false) {
    return 'workbook.errors.importNotAllowed';
  }
  return null;
}

export async function invalidateQuestionnaireQueries(queryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.overview, refetchType: 'all' }),
    queryClient.invalidateQueries({ queryKey: ['assessment', 'patterns'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.sections }),
    queryClient.invalidateQueries({ queryKey: ['assessment', 'questions'] }),
    queryClient.invalidateQueries({ queryKey: ['assessment', 'answers'] }),
    queryClient.invalidateQueries({ queryKey: ['assessment', 'weight-matrix'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.versions }),
    queryClient.invalidateQueries({ queryKey: ['assessment', 'top-three-profiles'] }),
  ]);
}

export async function invalidateTopThreeProfileWorkbookQueries(queryClient, { versionId, profileId } = {}) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['assessment', 'top-three-profiles'] }),
    versionId ? queryClient.invalidateQueries({ queryKey: queryKeys.topThreeProfileCoverage(versionId) }) : Promise.resolve(),
    profileId ? queryClient.invalidateQueries({ queryKey: queryKeys.topThreeProfile(profileId, versionId) }) : Promise.resolve(),
    queryClient.invalidateQueries({ queryKey: queryKeys.versions }),
    versionId ? queryClient.invalidateQueries({ queryKey: queryKeys.version(versionId) }) : Promise.resolve(),
    queryClient.invalidateQueries({ queryKey: queryKeys.overview }),
    queryClient.invalidateQueries({ queryKey: ['assessment', 'personal-summaries'] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.tags }),
    queryClient.invalidateQueries({ queryKey: ['assessment', 'result-items'] }),
  ]);
}

export const NEW_QUESTIONS_SUMMARY_ROWS = [
  { key: 'workbook.newQuestions.questionsCount', paths: ['questions_count', 'questions.created', 'questions_created', 'created_questions', 'question_creates'] },
  { key: 'workbook.newQuestions.answersCount', paths: ['answers_count', 'answers.created', 'answers_created', 'created_answers', 'answer_creates'] },
  { key: 'workbook.newQuestions.sectionsCount', paths: ['sections_count', 'sections.affected', 'affected_sections', 'sections_affected'] },
  { key: 'workbook.newQuestions.warningsCount', paths: ['warnings_count'] },
  { key: 'workbook.newQuestions.errorsCount', paths: ['errors_count'] },
];

const STALE_TEMPLATE_CODES = [
  'NEWQ_TEMPLATE_STALE',
  'NEWQ_PATTERN_SET_STALE',
  'TEMPLATE_STALE',
  'PATTERN_SET_STALE',
];

function collectCodes(values) {
  return (values || []).flatMap((item) => {
    if (!item) {
      return [];
    }
    if (typeof item === 'string') {
      return [item];
    }
    return [item.code, item.type, item.error_code].filter(Boolean);
  });
}

export function isStaleNewQuestionsTemplate(preview, error) {
  if (!preview && !error) {
    return false;
  }
  const payload = error?.payload && typeof error.payload === 'object' ? error.payload : {};
  const nested = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  const source = preview || {};
  const codes = [
    ...collectCodes(source.errors),
    ...collectCodes(source.warnings),
    ...collectCodes(nested.errors),
    ...collectCodes(nested.warnings),
    error?.code,
    nested.code,
    payload.code,
  ].filter(Boolean).map((code) => String(code).toUpperCase());
  if (source.templateStale || source.patternSetStale || nested.template_stale || nested.stale_template || nested.pattern_set_stale) {
    return true;
  }
  return STALE_TEMPLATE_CODES.some((code) => codes.includes(code));
}

export function isExpiredNewQuestionsPreview(error) {
  if (!error) {
    return false;
  }
  if (typeof error.isGone === 'function' && error.isGone()) {
    return true;
  }
  if (error.isStaleRevision?.()) {
    return false;
  }
  const code = String(error.code || error.payload?.code || error.errors?.code || '').toUpperCase();
  if (['PREVIEW_EXPIRED', 'COMMIT_TOKEN_EXPIRED', 'NEWQ_PREVIEW_EXPIRED', 'TOKEN_EXPIRED'].includes(code)) {
    return true;
  }
  const haystack = `${error.message || ''} ${JSON.stringify(error.errors || {})}`.toLowerCase();
  if ((error.status === 409 || error.status === 422) && /commit token|preview has expired|expired or used|invalid or expired|رمز الاعتماد/.test(haystack)) {
    return true;
  }
  return error.status === 422 && /expired|invalid preview|invalid token/.test(haystack);
}

function safeText(value) {
  if (value == null) {
    return '';
  }
  return String(value);
}

export function normalizeNewQuestionsErrors(errors) {
  return normalizeWorkbookErrors(errors).map((error) => ({
    ...error,
    questionRef: safeText(error.questionRef),
    field: safeText(error.column),
  }));
}

function readQuestionSummaries(source = {}) {
  const rows = source.questions || source.question_summaries || source.items || [];
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row, index) => {
    const weightsComplete = row.weights_complete ?? row.pattern_weights_complete ?? row.weightsComplete;
    const missingWeights = readCount(row, ['missing_weights', 'missing_pattern_weights'], 0);
    return {
      key: row.question_ref || row.questionRef || `question-${index}`,
      questionRef: safeText(row.question_ref || row.questionRef || row.ref || ''),
      sectionAr: safeText(row.section_ar || row.section_name_ar || row.section_title_ar || row.section || ''),
      questionAr: safeText(row.question_ar || row.text_ar || row.question_text_ar || ''),
      answerCount: Array.isArray(row.answers)
        ? row.answers.length
        : readCount(row, ['answer_count', 'answers_count'], 0),
      weightsComplete: weightsComplete == null ? missingWeights === 0 : Boolean(weightsComplete),
    };
  });
}

export function normalizeNewQuestionsPreview(data = {}, fallbackMessage = '') {
  const payload = data && typeof data === 'object' ? data : {};
  const nested = payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
    ? payload.data
    : payload;
  const errors = normalizeNewQuestionsErrors(nested.errors || payload.errors);
  const warnings = asWarningList(nested.warnings || payload.warnings);
  const summary = nested.summary || nested.counts || nested;
  const questionsCount = readCount(summary, ['questions_count', 'questions.created', 'questions_created', 'created_questions', 'question_creates'], 0);
  const answersCount = readCount(summary, ['answers_count', 'answers.created', 'answers_created', 'created_answers', 'answer_creates'], 0);
  const sectionsCount = readCount(summary, ['sections_count', 'sections.affected', 'affected_sections', 'sections_affected'], Array.isArray(nested.sections) ? nested.sections.length : 0);
  const templateStale = isStaleNewQuestionsTemplate({
    errors,
    warnings,
    templateStale: nested.template_stale === true || nested.stale_template === true,
    patternSetStale: nested.pattern_set_stale === true,
  });
  const commitToken = nested.commit_token || nested.preview_token || payload.commit_token || '';
  const valid = nested.valid === true || (nested.can_commit === true && errors.length === 0);
  return {
    workbookType: nested.workbook_type || payload.workbook_type || 'new_questions',
    schemaVersion: nested.schema_version ?? nested.template_version ?? nested.workbook_version ?? payload.schema_version ?? '',
    templateVersion: nested.template_version ?? nested.schema_version ?? nested.workbook_version ?? '',
    valid: valid && errors.length === 0 && !templateStale,
    canCommit: Boolean(valid && errors.length === 0 && !templateStale && commitToken),
    commitToken,
    questionsCount,
    answersCount,
    sectionsCount,
    warningsCount: readCount(summary, ['warnings_count'], warnings.length),
    errorsCount: readCount(summary, ['errors_count'], errors.length),
    questions: readQuestionSummaries(nested),
    warnings,
    errors,
    templateStale,
    patternSetStale: nested.pattern_set_stale === true || errors.some((error) => String(error.code).toUpperCase() === 'NEWQ_PATTERN_SET_STALE'),
    fileSha256: nested.file_sha256 || payload.file_sha256 || '',
    revision: nested.revision ?? nested.current_revision ?? payload.revision ?? null,
    applied: nested.applied || nested.applied_counts || nested.created || null,
    message: nested.message || payload.message || fallbackMessage,
    summary,
  };
}

export function hasNewQuestionsCellErrors(error) {
  const payload = error?.payload && typeof error.payload === 'object' ? error.payload : {};
  const nested = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  const candidates = [
    nested.errors?.cells,
    nested.cells,
    nested.errors,
    error?.errors?.cells,
    error?.errors,
  ];
  return candidates.some((list) => Array.isArray(list) && list.some((item) => (
    item && typeof item === 'object' && (item.row != null || item.sheet || item.column || item.cell || item.code || item.message)
  )));
}

export function newQuestionsPreviewFromApiError(error) {
  const payload = error?.payload && typeof error.payload === 'object' ? error.payload : {};
  const data = payload.data && typeof payload.data === 'object' ? payload.data : payload;
  const errorsSource = Array.isArray(data.errors?.cells)
    ? data.errors.cells
    : (Array.isArray(data.cells)
      ? data.cells
      : (Array.isArray(data.errors)
        ? data.errors
        : (Array.isArray(error?.errors?.cells)
          ? error.errors.cells
          : (Array.isArray(error?.errors) ? error.errors : error?.errors))));
  return normalizeNewQuestionsPreview({
    ...data,
    errors: errorsSource,
    valid: false,
    can_commit: false,
    commit_token: '',
    message: error?.message,
  }, error?.message);
}

export function newQuestionsSummaryRows(preview) {
  if (!preview) {
    return NEW_QUESTIONS_SUMMARY_ROWS.map((row) => ({ key: row.key, value: 0 }));
  }
  const createdQuestions = readCount(preview.applied || preview.summary, ['questions_created', 'questions.created', 'created_questions', 'question_creates'], preview.questionsCount);
  const createdAnswers = readCount(preview.applied || preview.summary, ['answers_created', 'answers.created', 'created_answers', 'answer_creates'], preview.answersCount);
  return [
    { key: 'workbook.newQuestions.questionsCount', value: createdQuestions },
    { key: 'workbook.newQuestions.answersCount', value: createdAnswers },
    { key: 'workbook.newQuestions.sectionsCount', value: preview.sectionsCount },
    { key: 'workbook.newQuestions.warningsCount', value: preview.warningsCount },
    { key: 'workbook.newQuestions.errorsCount', value: preview.errorsCount },
  ];
}

export async function invalidateNewQuestionsQueries(queryClient) {
  await Promise.all([
    invalidateQuestionnaireQueries(queryClient),
    queryClient.invalidateQueries({ queryKey: queryKeys.workbooks.newQuestionsPreview }),
    queryClient.invalidateQueries({ queryKey: queryKeys.workbooks.newQuestionsCommit }),
    queryClient.invalidateQueries({ queryKey: queryKeys.questions() }),
  ]);
}
